import { useInfiniteQuery } from '@tanstack/react-query'
import {
  OkendoResponse,
  ReviewAggregate,
  ReviewsResponse,
  StampedResponse,
  useReviewsQueryOptions,
} from './types'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useShopify } from '@/hooks/useShopify'

export const useReviewsQuery = ({
  productId,
  targetPageSize = 24,
  ...options
}: useReviewsQueryOptions) => {
  const { shop, integrations, locale } = useShopify()
  const { okendo_user_id } = integrations
  const { getFlag } = useFeatureFlags()
  const okendoFlag = getFlag('enable_okendo_reviews')

  const { data: { pages = [] } = {}, ...result } = useInfiniteQuery({
    queryKey: ['reviews', productId, shop],
    queryFn: async ({
      pageParam = okendoFlag ? undefined : 1,
    }): Promise<ReviewsResponse> => {
      if (okendoFlag) {
        // Fetch from Okendo API
        const baseUrl = `https://api.okendo.io/v1`

        const aggregateRes = await fetch(
          `${baseUrl}/stores/${okendo_user_id}/products/shopify-${productId}/review_aggregate`
        )
        const { reviewAggregate } = (await aggregateRes.json()) as {
          reviewAggregate: ReviewAggregate
        }

        const url = pageParam
          ? `${baseUrl}${pageParam}`
          : `${baseUrl}/stores/${okendo_user_id}/products/shopify-${productId}/reviews?limit=${targetPageSize}&locale=${locale}`
        const res = await fetch(url)
        const data = (await res.json()) as Omit<OkendoResponse, 'next'>

        const reviewsData = {
          reviews:
            data.reviews?.map((review) => {
              return {
                id: review.reviewId,
                rating: review.rating,
                title: review.title,
                body: review.body,
                dateCreated: review.dateCreated,
                reviewer: {
                  author: review.reviewer?.displayName,
                  isVerified: review.reviewer.isVerified ?? false,
                },
                original: review?.original,
              }
            }) ?? [],
          cursor: data.nextUrl,
          total: reviewAggregate.reviewCount,
        } as ReviewsResponse

        return reviewsData
      } else {
        // Fetch from Stamped API
        const page: number = Number(pageParam) ?? 1
        const res = await fetch(
          `https://stamped.io/api/widget/reviews?productId=${productId}&storeUrl=${shop}&page=${pageParam}&take=${targetPageSize}`
        )
        const data = (await res.json()) as Omit<StampedResponse, 'cursor'>
        const cursor = data.total > page * targetPageSize ? page + 1 : undefined
        const reviewsData = {
          reviews: data.data?.map((review) => {
            return {
              id: review.id,
              rating: review.reviewRating,
              title: review.reviewTitle,
              body: review.reviewMessage,
              dateCreated: review.dateCreated,
              reviewer: {
                author: review.author,
                isVerified: Boolean(review.reviewVerifiedType),
              },
            }
          }),
          cursor: cursor,
          total: data.total,
        } as ReviewsResponse
        return reviewsData
      }
    },
    initialPageParam: okendoFlag ? undefined : 1,
    getNextPageParam: (lastPage: ReviewsResponse) => lastPage.cursor,
    ...options,
  })
  return { data: { pages }, ...result }
}
